import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import { handleGetRequest } from "../../services/GetTemplate";
import {
    approveShift,
    fetchAdminActiveShifts,
    fetchAdminPendingApprovals,
    fetchAdminShiftHistory,
    formatHours,
    GATED_SHIFT_ROLES,
    shiftStatusBadgeClass,
    shiftStatusLabel,
} from "../../services/shiftService";
import styles from "./shifts.module.scss";

const RANGE_OPTIONS = [
    { label: "Today", value: "day" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
    { label: "This Year", value: "year" },
];

const staffName = (staff) => (staff ? `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || staff.email : "Unknown");

const mapsLink = (latitude, longitude) => `https://www.google.com/maps?q=${latitude},${longitude}`;

const LocationLink = ({ point, stopClick }) => {
    if (!point) return <span>-</span>;
    return (
        <a
            href={mapsLink(point.latitude, point.longitude)}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => stopClick && e.stopPropagation()}
        >
            {point.latitude.toFixed(4)}, {point.longitude.toFixed(4)}
        </a>
    );
};

const AdminShifts = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [activeShifts, setActiveShifts] = useState([]);
    const [loadingActive, setLoadingActive] = useState(true);
    const [now, setNow] = useState(Date.now());

    const [pendingShifts, setPendingShifts] = useState([]);
    const [loadingPending, setLoadingPending] = useState(true);
    const [approvingId, setApprovingId] = useState(null);

    const [staffOptions, setStaffOptions] = useState([]);
    const [staffId, setStaffId] = useState(null);
    const [range, setRange] = useState("week");
    const [dateRange, setDateRange] = useState(null);
    const [historyShifts, setHistoryShifts] = useState([]);
    const [summary, setSummary] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    const loadActive = async () => {
        setLoadingActive(true);
        const res = await fetchAdminActiveShifts();
        setActiveShifts(res?.data || []);
        setLoadingActive(false);
    };

    const loadPending = async () => {
        setLoadingPending(true);
        const res = await fetchAdminPendingApprovals();
        setPendingShifts(res?.data || []);
        setLoadingPending(false);
    };

    const handleApprove = (row) => {
        confirmDialog({
            message: `Approve an extra shift today for ${staffName(row.staff)}? This will start their shift immediately.`,
            header: "Approve Shift",
            icon: "pi pi-question-circle",
            accept: async () => {
                setApprovingId(row._id);
                try {
                    const res = await approveShift(dispatch, row._id);
                    if (res?.success) {
                        loadPending();
                        loadActive();
                    }
                } finally {
                    setApprovingId(null);
                }
            },
        });
    };

    const loadStaffOptions = async () => {
        const res = await handleGetRequest("/allUsers");
        const staff = (res?.data || []).filter((u) => GATED_SHIFT_ROLES.includes(u.role));
        setStaffOptions(staff.map((u) => ({ label: `${staffName(u)} (${u.role})`, value: u._id })));
    };

    const loadHistory = async () => {
        setLoadingHistory(true);
        let res;
        if (dateRange && dateRange.length === 2 && dateRange[1]) {
            res = await fetchAdminShiftHistory(staffId, null, dateRange[0].toISOString(), dateRange[1].toISOString());
        } else {
            res = await fetchAdminShiftHistory(staffId, range);
        }
        setHistoryShifts(res?.data?.shifts || []);
        setSummary(res?.data?.summary || []);
        setLoadingHistory(false);
    };

    useEffect(() => {
        loadActive();
        loadPending();
        loadStaffOptions();
        const tick = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(tick);
    }, []);

    useEffect(() => {
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staffId, range]);

    const activeStats = useMemo(
        () => ({
            count: activeShifts.length,
        }),
        [activeShifts],
    );

    const pendingCount = pendingShifts.length;

    const nameBody = (row) => staffName(row.staff);
    const roleBody = (row) => row.role;
    const clockInBody = (row) => (row.clockIn?.time ? new Date(row.clockIn.time).toLocaleString() : "-");
    const clockOutBody = (row) => (row.clockOut?.time ? new Date(row.clockOut.time).toLocaleString() : "-");
    const elapsedBody = (row) => {
        const diffMs = Math.max(0, now - new Date(row.clockIn.time).getTime());
        return formatHours(diffMs / (1000 * 60 * 60));
    };
    const clockInLocationBody = (row) => <LocationLink point={row.clockIn} stopClick />;
    const clockOutLocationBody = (row) => <LocationLink point={row.clockOut} stopClick />;
    const lastPingBody = (row) => {
        const last = row.geoPings?.[row.geoPings.length - 1];
        if (!last) return <span title="Recorded once an hour has passed since clock-in">Awaiting first hourly ping</span>;
        return <LocationLink point={last} stopClick />;
    };
    const hoursBody = (row) => formatHours(row.totalHours);
    const breaksBody = (row) => (row.breaks?.length ? `${row.breaks.length} (${formatHours(row.breaks.reduce((s, b) => s + (b.hours || 0), 0))})` : "-");
    const statusBody = (row) => <span className={`${styles.badge} ${styles[shiftStatusBadgeClass(row.status)]}`}>{shiftStatusLabel(row.status)}</span>;
    const viewBody = (row) => (
        <Button
            icon="pi pi-eye"
            label="View"
            className="p-button-text p-button-sm"
            onClick={(e) => {
                e.stopPropagation();
                history.push(`/admin/shifts/${row._id}`);
            }}
        />
    );
    const approveBody = (row) => (
        <Button
            icon={approvingId === row._id ? "pi pi-spin pi-spinner" : "pi pi-check"}
            label="Approve"
            className="p-button-success p-button-sm"
            disabled={approvingId === row._id}
            onClick={(e) => {
                e.stopPropagation();
                handleApprove(row);
            }}
        />
    );
    const goToDetail = (e) => history.push(`/admin/shifts/${e.data._id}`);
    const openDetail = (id) => history.push(`/admin/shifts/${id}`);

    const activeCard = (row) => (
        <DataCard
            key={row._id}
            accent="green"
            title={staffName(row.staff)}
            subtitle={row.role}
            badge={<span className={`${styles.badge} ${styles.badgeActive}`}>{elapsedBody(row)}</span>}
            fields={[
                { label: "Clocked In", value: clockInBody(row) },
                { label: "Clock-In Location", value: clockInLocationBody(row) },
                { label: "Last Ping", value: lastPingBody(row) },
            ]}
            onClick={() => openDetail(row._id)}
        />
    );

    const historyCard = (row) => (
        <DataCard
            key={row._id}
            accent={row.status === "active" ? "green" : "slate"}
            title={staffName(row.staff)}
            subtitle={row.role}
            badge={statusBody(row)}
            fields={[
                { label: "Clock In", value: clockInBody(row) },
                { label: "In Location", value: clockInLocationBody(row) },
                { label: "Clock Out", value: clockOutBody(row) },
                { label: "Out Location", value: clockOutLocationBody(row) },
                { label: "Total Hours", value: hoursBody(row) },
                { label: "Breaks", value: breaksBody(row) },
            ]}
            onClick={() => openDetail(row._id)}
        />
    );

    const pendingCard = (row) => (
        <DataCard
            key={row._id}
            accent="amber"
            title={staffName(row.staff)}
            subtitle={row.role}
            badge={<span className={`${styles.badge} ${styles.badgePending}`}>Pending</span>}
            fields={[
                { label: "Requested", value: clockInBody(row) },
                { label: "Location", value: clockInLocationBody(row) },
            ]}
            actions={
                <>
                    {approveBody(row)}
                    {viewBody(row)}
                </>
            }
        />
    );

    return (
        <div className={styles.page}>
            <ConfirmDialog />

            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-clock" />
                    </div>
                    <div>
                        <h2>Shifts</h2>
                        <p className={styles.subtitle}>Track who's on shift and review historic hours worked</p>
                    </div>
                </div>
            </div>

            <TabView>
                <TabPanel header={`Active Now (${activeStats.count})`} leftIcon="pi pi-users">
                    <ResponsiveView
                        value={activeShifts}
                        loading={loadingActive}
                        loadingText="Loading active shifts..."
                        emptyMessage="No one is currently clocked in."
                        storageKey="adminShifts.active"
                        renderCard={activeCard}
                    >
                        <DataTable
                            value={activeShifts}
                            paginator
                            rows={20}
                            className="datatable-responsive custom-datatable"
                            emptyMessage="No one is currently clocked in."
                            selectionMode="single"
                            onRowClick={goToDetail}
                            rowHover
                        >
                            <Column header="Staff" body={nameBody} />
                            <Column header="Role" body={roleBody} />
                            <Column header="Clocked In" body={clockInBody} />
                            <Column header="Elapsed" body={elapsedBody} />
                            <Column header="Clock-In Location" body={clockInLocationBody} />
                            <Column header="Last Hourly Ping" body={lastPingBody} />
                            <Column header="" body={viewBody} style={{ width: "100px" }} />
                        </DataTable>
                    </ResponsiveView>
                </TabPanel>

                <TabPanel header="History" leftIcon="pi pi-history">
                    <div className={styles.filtersSection}>
                        <div className={styles.filterGroup}>
                            <label htmlFor="staff">Staff</label>
                            <Dropdown id="staff" value={staffId} options={staffOptions} onChange={(e) => setStaffId(e.value)} placeholder="All Staff" showClear style={{ minWidth: "220px" }} />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>Quick Range</label>
                            <div className={styles.quickRangeRow}>
                                {RANGE_OPTIONS.map((opt) => (
                                    <Button
                                        key={opt.value}
                                        label={opt.label}
                                        className={`${styles.quickRangeBtn} ${range === opt.value && !dateRange ? styles.activeRange : ""}`}
                                        onClick={() => {
                                            setDateRange(null);
                                            setRange(opt.value);
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className={styles.filterGroup}>
                            <label htmlFor="dateRange">Custom Range</label>
                            <Calendar id="dateRange" value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput placeholder="Select date range" />
                        </div>
                        <div className={styles.filterGroup}>
                            <Button label="Apply" icon="pi pi-search" onClick={loadHistory} className={styles.primaryButton} />
                        </div>
                    </div>

                    {summary.length > 0 && (
                        <div className={styles.statsRow}>
                            {summary.map((s) => (
                                <div className={styles.statCard} key={s.staff?._id || staffName(s.staff)}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-user" />
                                    </div>
                                    <div>
                                        <div className={styles.statValue}>{formatHours(s.totalHours)}</div>
                                        <div className={styles.statLabel}>{staffName(s.staff)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <ResponsiveView
                        value={historyShifts}
                        loading={loadingHistory}
                        loadingText="Loading shift history..."
                        emptyMessage="No shifts found for this period."
                        storageKey="adminShifts.history"
                        renderCard={historyCard}
                    >
                        <DataTable
                            value={historyShifts}
                            paginator
                            rows={20}
                            rowsPerPageOptions={[20, 50, 100]}
                            className="datatable-responsive custom-datatable"
                            emptyMessage="No shifts found for this period."
                            selectionMode="single"
                            onRowClick={goToDetail}
                            rowHover
                        >
                            <Column header="Staff" body={nameBody} />
                            <Column header="Role" body={roleBody} />
                            <Column header="Clock In" body={clockInBody} />
                            <Column header="Clock-In Location" body={clockInLocationBody} />
                            <Column header="Clock Out" body={clockOutBody} />
                            <Column header="Clock-Out Location" body={clockOutLocationBody} />
                            <Column header="Total Hours" body={hoursBody} />
                            <Column header="Breaks" body={breaksBody} />
                            <Column header="Status" body={statusBody} />
                            <Column header="" body={viewBody} style={{ width: "100px" }} />
                        </DataTable>
                    </ResponsiveView>
                </TabPanel>

                <TabPanel header={`Pending Approval (${pendingCount})`} leftIcon="pi pi-hourglass">
                    <ResponsiveView
                        value={pendingShifts}
                        loading={loadingPending}
                        loadingText="Loading pending approvals..."
                        emptyMessage="No shifts are waiting for approval."
                        storageKey="adminShifts.pending"
                        renderCard={pendingCard}
                    >
                        <DataTable
                            value={pendingShifts}
                            paginator
                            rows={20}
                            className="datatable-responsive custom-datatable"
                            emptyMessage="No shifts are waiting for approval."
                            selectionMode="single"
                            onRowClick={goToDetail}
                            rowHover
                        >
                            <Column header="Staff" body={nameBody} />
                            <Column header="Role" body={roleBody} />
                            <Column header="Requested Clock-In" body={clockInBody} />
                            <Column header="Location" body={clockInLocationBody} />
                            <Column header="" body={approveBody} style={{ width: "140px" }} />
                            <Column header="" body={viewBody} style={{ width: "100px" }} />
                        </DataTable>
                    </ResponsiveView>
                </TabPanel>
            </TabView>
        </div>
    );
};

export default AdminShifts;
